import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import MapComponent from "./Map";
import AddProjectPanel from "./AddProjectPanel";
import "./styles.css";
import {
  getAddProjectPlanetTileTemplate,
  getAddProjectSatelliteTileTemplate,
  getDefaultMapTileTemplate,
  getSatelliteTilesAvailable,
  getTilesLimits,
} from "../../api/FlaskAPI";
import {
  setAddProjectPlanetTileTemplate,
  setAddProjectSatelliteTileTemplate,
  setDefaultMapTileTemplate,
  setSatelliteTilesAvailable,
  setTilesLimits,
} from "../../reducers/MapSlice";

const TOKEN_STORAGE_KEY = "addProjectToken";

function useProjectToken() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() => {
    return (
      location.state?.projectToken || sessionStorage.getItem(TOKEN_STORAGE_KEY)
    );
  });

  useEffect(() => {
    if (location.state?.projectToken) {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, location.state.projectToken);
    }
  }, [location.state]);

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [token, navigate]);

  const clearToken = useCallback(() => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
  }, []);

  return { token, clearToken };
}

export default function AddProject() {
  const dispatch = useDispatch();
  const { token, clearToken } = useProjectToken();
  const [areas, setAreas] = useState(null);
  const [boundaryMethod, setBoundaryMethod] = useState("upload");

  useEffect(() => {
    const init = async () => {
      try {
        const [limitsRes, availabilityRes] = await Promise.all([
          getTilesLimits(),
          getSatelliteTilesAvailable().catch(() => ({
            data: {
              satellite_tiles: false,
              planet_tiles: false,
              defaultmap_tiles: false,
            },
          })),
        ]);

        if (limitsRes.status === 200) {
          dispatch(setTilesLimits(limitsRes.data));
        }

        const availability = availabilityRes?.data;
        dispatch(setSatelliteTilesAvailable(availability));

        const [satelliteRes, planetRes, defaultmapRes] = await Promise.all([
          getAddProjectSatelliteTileTemplate(token).catch(() => null),
          getAddProjectPlanetTileTemplate(token).catch(() => null),
          getDefaultMapTileTemplate().catch(() => null),
        ]);

        if (satelliteRes?.status === 200) {
          dispatch(
            setAddProjectSatelliteTileTemplate({
              tileUrlTemplate: satelliteRes.data.tile_url_template,
              attribution: satelliteRes.data.attribution,
              expiresAt: satelliteRes.data.expires_at,
            }),
          );
        }

        if (planetRes?.status === 200) {
          dispatch(
            setAddProjectPlanetTileTemplate({
              tileUrlTemplate: planetRes.data.tile_url_template,
              attribution: planetRes.data.attribution,
              expiresAt: planetRes.data.expires_at,
            }),
          );
        }

        if (defaultmapRes?.status === 200) {
          dispatch(
            setDefaultMapTileTemplate({
              tileUrlTemplate: defaultmapRes.data.tile_url_template,
              attribution: defaultmapRes.data.attribution,
              expiresAt: defaultmapRes.data.expires_at,
            }),
          );
        }
      } catch (error) {
        console.error("Error initializing AddProject map settings:", error);
      }
    };

    init();
  }, [dispatch, token]);

  const handleAreasChange = useCallback((newAreas) => {
    setAreas(newAreas);
  }, []);

  const handleBoundaryMethodChange = useCallback((method) => {
    setBoundaryMethod(method);
  }, []);

  if (!token) {
    return null;
  }

  return (
    <div className="add-project-container">
      <MapComponent
        token={token}
        areas={areas}
        onAreasChange={handleAreasChange}
        boundaryMethod={boundaryMethod}
      />

      <Box
        sx={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 1000,
          pointerEvents: "auto",
        }}
      >
        <AddProjectPanel
          token={token}
          areas={areas}
          onAreasChange={handleAreasChange}
          boundaryMethod={boundaryMethod}
          onBoundaryMethodChange={handleBoundaryMethodChange}
          onProjectCreated={clearToken}
        />
      </Box>
    </div>
  );
}
